import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { detectPlatform } from "../../platform.js";
import { splitVirtualId } from "../../lib/virtual-id.js";
import { useCalendarFocusStore, useSettingsStore, useTasksStore } from "../../stores/index.js";
import { SNOOZE_ACTIONS } from "./adapters/capacitor.js";
import { notificationCalendarTarget } from "./notification-calendar-target.js";

/**
 * Android only: respond to taps on a fired reminder. Capacitor delivers the
 * tapped action (even on a cold start, queued until a listener attaches) via
 * `localNotificationActionPerformed`.
 *
 * - "Snooze 10 min / 1 hour" buttons: read the task ids stashed in the
 *   notification's `extra`, compute the wake time, snooze each task/occurrence.
 * - Body tap (`actionId === "tap"`): open the calendar with the task's
 *   occurrence scrolled into view + flash-highlighted (same jump as a search
 *   "View on calendar" click). Plain fallback notifications carry no `extra`,
 *   so a tap on those just opens the app as before.
 *
 * Mount once, in the always-present Shell (inside the router — navigation).
 *
 * No-op off Capacitor — web/desktop notifications can't carry action buttons
 * until the R2 service worker lands; tapping a notification just opens the app.
 */
export function useNotificationActions(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (detectPlatform() !== "capacitor") return;
    let removed = false;
    let remove: (() => void) | null = null;

    void import("@capacitor/local-notifications").then(({ LocalNotifications }) =>
      LocalNotifications.addListener("localNotificationActionPerformed", (e) => {
        const ids = (e.notification.extra as { taskIds?: unknown } | undefined)?.taskIds;
        if (!Array.isArray(ids) || ids.length === 0) return;

        const minutes = SNOOZE_ACTIONS[e.actionId as keyof typeof SNOOZE_ACTIONS];
        if (minutes) {
          const atIso = new Date(Date.now() + minutes * 60_000).toISOString();
          void (async () => {
            // Cold start: Capacitor queues the tapped action and delivers it as
            // soon as this listener attaches — which can beat store hydration, and
            // snooze() silently no-ops on a task it can't find. hydrate() is
            // deduped, so awaiting it here is free when the store is already live.
            await useTasksStore.getState().hydrate();
            const store = useTasksStore.getState();
            for (const raw of ids) {
              const { sourceId, occIso } = splitVirtualId(String(raw));
              if (occIso) await store.snoozeOccurrence(sourceId, occIso, atIso);
              else await store.snooze(sourceId, atIso);
            }
          })();
          return;
        }

        if (e.actionId !== "tap") return; // unknown custom action
        void (async () => {
          // Same cold-start rule as above — and settings for the last-used
          // calendar sub-view (both hydrates are deduped).
          await Promise.all([useTasksStore.getState().hydrate(), useSettingsStore.getState().hydrate()]);
          const store = useTasksStore.getState();
          const target = notificationCalendarTarget(String(ids[0]), store.tasks, store.overrides);
          if (!target) return;
          useCalendarFocusStore.getState().focus(new Date(target.dueIso), target.highlightId);
          navigate(`/calendar/${useSettingsStore.getState().lastCalendarView}`);
        })();
      }).then((handle) => {
        if (removed) void handle.remove();
        else remove = () => void handle.remove();
      }),
    );

    return () => {
      removed = true;
      remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
